import { useId } from 'react';
import type { MetricPoint } from '../api.js';

interface Props {
  points: MetricPoint[];
  color: string;
  width?: number;
  height?: number;
}

/**
 * A thin instrument trace for a stat tile — a 2px line over a faint fill,
 * baseline-anchored. Hand-rolled SVG (no per-tile ECharts). A flat or single
 * point renders a centered baseline so an empty vital still reads as "quiet",
 * not broken.
 */
export default function Sparkline({ points, color, width = 200, height = 30 }: Props): JSX.Element {
  const gid = useId();
  const pad = 2;
  const w = width;
  const h = height;

  const values = points.map((p) => p.v).filter((v) => Number.isFinite(v));
  if (values.length === 0) {
    return (
      <svg className="tile__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <line x1={pad} y1={h / 2} x2={w - pad} y2={h / 2} stroke="var(--grid)" strokeWidth={1} />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const n = values.length;
  const x = (i: number): number => (n === 1 ? w / 2 : pad + (i / (n - 1)) * (w - pad * 2));
  const y = (v: number): number => h - pad - ((v - min) / span) * (h - pad * 2);

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(n - 1).toFixed(1)},${h - pad} L${x(0).toFixed(1)},${h - pad} Z`;
  const lastX = x(n - 1);
  const lastY = y(values[n - 1]!);

  return (
    <svg className="tile__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={2.4} fill={color} />
    </svg>
  );
}
